// src/hooks/useSaveUserAddress.js
import { useCallback, useState } from 'react';
import { addDoc, collection, doc, updateDoc } from 'firebase/firestore';

/**
 * Persistence-only hook for saving/updating the first user address.
 * - Depends on external state passed in (addressFieldsState)
 * - Returns saveAddress() and isSaving
 */
export default function useSaveUserAddress({
  db,
  uid,
  globalData,
  setGlobalData,
  toast,
  LogError,
}) {
  const [isSaving, setIsSaving] = useState(false);

  const saveAddress = useCallback(
    async (addressFieldsState) => {
      const { street, city, region, country, zip } = addressFieldsState || {};

      // ---- Debug the incoming payload ----
      console.groupCollapsed('[Address Save] Incoming');
      console.log(
        'addrQuery-like label:',
        [street, city].filter(Boolean).join(', ')
      );
      console.table({
        street,
        city,
        region,
        country,
        zip,
        id: globalData?.address?.[0]?.id ?? null,
      });
      console.groupEnd();

      // ---- Validate required fields (and show exactly what's missing) ----
      const fields = { street, city, region, country, zip };

      console.groupCollapsed('[Address Validation]');
      Object.entries(fields).forEach(([k, v]) =>
        console.log(`${k}:`, v, '→ trimmed:', String(v ?? '').trim())
      );
      console.groupEnd();

      const missing = Object.entries(fields)
        .filter(([_, v]) => !String(v ?? '').trim())
        .map(([k]) => k);

      if (missing.length > 0) {
        console.warn('⚠️ Missing address fields:', missing);
        toast?.error?.(
          `All address fields must be filled out. Missing: ${missing.join(
            ', '
          )}`
        );
        return;
      }

      setIsSaving(true);
      try {
        const userAddressCol = collection(db, `users/${uid}/address`);

        if (globalData?.address?.[0]?.id) {
          // ---- Update existing address doc ----
          const existingId = globalData.address[0].id;
          const existingRef = doc(db, `users/${uid}/address/${existingId}`);
          await updateDoc(existingRef, {
            street: String(street).trim(),
            city: String(city).trim(),
            region: String(region).trim(),
            country: String(country).trim(),
            zip: String(zip).trim(),
            updatedDate: new Date(),
          });

          setGlobalData?.((prev) => ({
            ...prev,
            address: [
              {
                ...prev.address[0],
                street: String(street).trim(),
                city: String(city).trim(),
                region: String(region).trim(),
                country: String(country).trim(),
                zip: String(zip).trim(),
              },
            ],
          }));

          toast?.success?.('Address updated successfully!');
        } else {
          // ---- Create new address doc ----
          const newRef = await addDoc(userAddressCol, {
            street: String(street).trim(),
            city: String(city).trim(),
            region: String(region).trim(),
            country: String(country).trim(),
            zip: String(zip).trim(),
            createdDate: new Date(),
          });

          setGlobalData?.((prev) => ({
            ...prev,
            address: [
              {
                id: newRef.id,
                street: String(street).trim(),
                city: String(city).trim(),
                region: String(region).trim(),
                country: String(country).trim(),
                zip: String(zip).trim(),
              },
            ],
          }));

          toast?.success?.('Address added successfully!');
        }
      } catch (error) {
        console.error('Error saving address:', error);
        LogError?.('Error', {
          error: {
            message: error?.message || 'Unknown error',
            stack: error?.stack || 'No stack trace available',
            name: error?.name || 'Error',
          },
          userName: globalData?.name || 'No Name',
          uid: uid || 'No ID',
          functionCode: 'saveAddress',
          page: 'useSaveUserAddress',
        });
        toast?.error?.('Failed to save/update address.');
      } finally {
        setIsSaving(false);
      }
    },
    [db, uid, globalData, setGlobalData, toast, LogError]
  );

  return { saveAddress, isSaving };
}
